(() => {
  'use strict';

  const loadKey = '__kinoraTestimonialCarouselLoaded';
  if (window[loadKey]) return;
  window[loadKey] = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const modulo = (value, length) => ((value % length) + length) % length;

  function initCarousel(root) {
    if (root.dataset.knTestimonialCarouselReady === 'true') return;
    const track = root.querySelector('[data-kn-testimonial-track]');
    if (!track) return;
    const originals = Array.from(track.children).filter(card => card.matches('[data-kn-testimonial-card]'));
    if (!originals.length) return;
    root.dataset.knTestimonialCarouselReady = 'true';

    const listeners = new AbortController();
    const signal = listeners.signal;
    const expanded = new Set();
    let before = [];
    let after = [];
    let cycleStart = 0;
    let cycleWidth = 0;
    let loopInset = 0;
    let position = 0;
    let lastWritten = 0;
    let previousFrame = 0;
    let autoFrame = 0;
    let manualFrame = 0;
    let layoutFrame = 0;
    let resumeTimer = 0;
    let scrollTimer = 0;
    let ready = false;
    let disposed = false;
    let visible = !('IntersectionObserver' in window);
    let pointerHeld = false;
    let hovering = false;
    let focused = false;
    let nativeScrolling = false;
    let resumeAt = 0;

    originals.forEach((card, index) => { card.dataset.knTestimonialIndex = String(index); });

    function cloneSet(side) {
      return originals.map((card, index) => {
        const clone = card.cloneNode(true);
        const suffix = '--' + side + '-' + index;
        clone.dataset.knTestimonialClone = side;
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach(element => { element.id += suffix; });
        clone.querySelectorAll('[aria-controls]').forEach(element => {
          element.setAttribute('aria-controls', element.getAttribute('aria-controls') + suffix);
        });
        clone.querySelectorAll('a,button,[tabindex]').forEach(element => { element.tabIndex = -1; });
        clone.querySelectorAll('button[data-kn-testimonial-expand]').forEach(button => {
          const label = document.createElement('span');
          for (const attribute of button.attributes) {
            if (!['type', 'tabindex'].includes(attribute.name)) label.setAttribute(attribute.name, attribute.value);
          }
          label.textContent = button.textContent;
          button.replaceWith(label);
        });
        return clone;
      });
    }

    if (originals.length > 1) {
      before = cloneSet('before');
      after = cloneSet('after');
      track.prepend(...before);
      track.append(...after);
    } else {
      root.classList.add('is-single');
    }

    // The large relocation must never be animated by CSS or corrected by anchoring.
    track.style.scrollBehavior = 'auto';
    track.style.scrollSnapType = 'none';
    track.style.overflowAnchor = 'none';

    function loopPosition(value) {
      const start = cycleStart - loopInset;
      return cycleWidth ? start + modulo(value - start, cycleWidth) : value;
    }

    function writePosition(value) {
      position = loopPosition(value);
      track.scrollLeft = position;
      // Keep the fractional coordinate separate from browsers' rounded scrollLeft.
      lastWritten = track.scrollLeft;
    }

    function stopAuto() {
      cancelAnimationFrame(autoFrame);
      autoFrame = 0;
      previousFrame = 0;
      clearTimeout(resumeTimer);
      resumeTimer = 0;
    }

    function stopManual() {
      cancelAnimationFrame(manualFrame);
      manualFrame = 0;
    }

    function canRunAuto() {
      return ready && cycleWidth > 0 && !disposed && root.isConnected && visible &&
        !document.hidden && !reducedMotion.matches && !pointerHeld && !hovering &&
        !focused && !nativeScrolling && !manualFrame && expanded.size === 0;
    }

    function updateAuto() {
      stopAuto();
      if (!canRunAuto()) return;
      const delay = resumeAt - Date.now();
      if (delay > 0) resumeTimer = window.setTimeout(updateAuto, delay + 16);
      else autoFrame = requestAnimationFrame(tick);
    }

    function pauseAfterInteraction() {
      resumeAt = Date.now() + 10000;
      updateAuto();
    }

    function tick(timestamp) {
      autoFrame = 0;
      if (!root.isConnected) { cleanup(); return; }
      if (!canRunAuto()) { stopAuto(); return; }
      // Do not replay missed time after a busy frame or background tab.
      const elapsed = previousFrame ? Math.min(timestamp - previousFrame, 40) : 0;
      previousFrame = timestamp;
      writePosition(position + elapsed * 0.032);
      autoFrame = requestAnimationFrame(tick);
    }

    function refreshMetrics() {
      layoutFrame = 0;
      if (disposed || !root.isConnected || originals.length < 2) return;
      // DOMRect retains fractional card widths. A cycle includes the final gap.
      const firstLeft = originals[0].getBoundingClientRect().left;
      const nextStart = firstLeft - before[0].getBoundingClientRect().left;
      const nextWidth = after[0].getBoundingClientRect().left - firstLeft;
      const nextInset = Math.max(0, track.clientWidth - originals[0].getBoundingClientRect().width);
      if (nextWidth <= 0 || !track.clientWidth) return;
      if (ready && Math.abs(nextWidth - cycleWidth) < 0.01 && Math.abs(nextStart - cycleStart) < 0.01 && Math.abs(nextInset - loopInset) < 0.01) return;
      const phase = cycleWidth ? modulo(position - cycleStart, cycleWidth) / cycleWidth : 0;
      stopManual();
      cycleStart = nextStart;
      cycleWidth = nextWidth;
      loopInset = nextInset;
      writePosition(cycleStart + phase * cycleWidth);
      ready = true;
      root.classList.add('is-ready');
      updateAuto();
    }

    function requestMetrics() {
      if (!layoutFrame && !disposed) layoutFrame = requestAnimationFrame(refreshMetrics);
    }

    function animateBy(distance) {
      stopManual();
      stopAuto();
      clearTimeout(scrollTimer);
      nativeScrolling = false;
      resumeAt = Date.now() + 10000;
      const from = position;
      if (reducedMotion.matches) {
        writePosition(from + distance);
        updateAuto();
        return;
      }
      let started;
      function step(timestamp) {
        if (started === undefined) started = timestamp;
        const progress = Math.min((timestamp - started) / 440, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        writePosition(from + distance * eased);
        if (progress < 1) manualFrame = requestAnimationFrame(step);
        else { manualFrame = 0; updateAuto(); }
      }
      manualFrame = requestAnimationFrame(step);
    }

    function advance(direction) {
      if (!cycleWidth) return;
      const step = originals.length > 1 ?
        originals[1].getBoundingClientRect().left - originals[0].getBoundingClientRect().left :
        originals[0].getBoundingClientRect().width;
      animateBy(step * direction);
    }

    root.querySelector('[data-kn-testimonial-prev]')?.addEventListener('click', () => advance(-1), { signal });
    root.querySelector('[data-kn-testimonial-next]')?.addEventListener('click', () => advance(1), { signal });

    root.addEventListener('click', event => {
      const button = event.target.closest('[data-kn-testimonial-expand]');
      if (!button || !root.contains(button)) return;
      const card = button.closest('[data-kn-testimonial-card]');
      const index = card?.dataset.knTestimonialIndex;
      if (index === undefined) return;
      const shouldExpand = !expanded.has(index);
      if (shouldExpand) expanded.add(index);
      else expanded.delete(index);
      root.querySelectorAll('[data-kn-testimonial-card]').forEach(matchingCard => {
        if (matchingCard.dataset.knTestimonialIndex !== index) return;
        matchingCard.classList.toggle('is-expanded', shouldExpand);
        const toggle = matchingCard.querySelector('[data-kn-testimonial-expand]');
        if (toggle) {
          toggle.textContent = shouldExpand ? 'See less' : 'See more';
          toggle.setAttribute('aria-expanded', String(shouldExpand));
        }
      });
      stopManual();
      pauseAfterInteraction();
      requestMetrics();
      // Expanding a card must not scroll the entire page vertically.
      if (shouldExpand) {
        const viewport = track.getBoundingClientRect();
        const bounds = card.getBoundingClientRect();
        if (bounds.left < viewport.left) animateBy(bounds.left - viewport.left);
        else if (bounds.right > viewport.right) animateBy(bounds.right - viewport.right);
      }
    }, { signal });

    function settleNativeScroll() {
      clearTimeout(scrollTimer);
      if (pointerHeld) return;
      nativeScrolling = false;
      // Rebase after native momentum ends. Writing during a swipe breaks iOS momentum.
      writePosition(track.scrollLeft);
      pauseAfterInteraction();
    }

    track.addEventListener('scroll', () => {
      if (disposed || !ready) return;
      if (!pointerHeld && Math.abs(track.scrollLeft - lastWritten) < 1) return;
      position = track.scrollLeft;
      nativeScrolling = true;
      stopAuto();
      stopManual();
      clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(settleNativeScroll, 180);
    }, { passive: true, signal });

    root.addEventListener('pointerdown', () => {
      pointerHeld = true;
      stopAuto();
      stopManual();
    }, { passive: true, signal });
    const releasePointer = () => {
      if (!pointerHeld) return;
      pointerHeld = false;
      resumeAt = Date.now() + 10000;
      if (nativeScrolling) {
        clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(settleNativeScroll, 180);
      } else updateAuto();
    };
    window.addEventListener('pointerup', releasePointer, { signal });
    window.addEventListener('pointercancel', releasePointer, { signal });
    root.addEventListener('pointerenter', event => {
      if (event.pointerType !== 'mouse') return;
      hovering = true;
      stopAuto();
    }, { signal });
    root.addEventListener('pointerleave', event => {
      if (event.pointerType !== 'mouse') return;
      hovering = false;
      pauseAfterInteraction();
    }, { signal });
    track.addEventListener('wheel', () => {
      stopManual();
      pauseAfterInteraction();
    }, { passive: true, signal });
    root.addEventListener('focusin', () => { focused = true; stopAuto(); }, { signal });
    root.addEventListener('focusout', () => {
      queueMicrotask(() => {
        focused = root.contains(document.activeElement);
        if (!focused) pauseAfterInteraction();
      });
    }, { signal });
    track.addEventListener('keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      advance(event.key === 'ArrowLeft' ? -1 : 1);
    }, { signal });

    const visibilityObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.05);
      updateAuto();
    }, { threshold: 0.05 }) : null;
    visibilityObserver?.observe(track);

    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(requestMetrics) : null;
    resizeObserver?.observe(track);
    originals.forEach(card => resizeObserver?.observe(card));
    track.querySelectorAll('img').forEach(img => {
      img.addEventListener('load', requestMetrics, { once: true, signal });
      if (img.complete && img.decode) img.decode().then(requestMetrics, () => {});
    });
    document.fonts?.ready.then(requestMetrics);
    window.addEventListener('resize', requestMetrics, { passive: true, signal });
    document.addEventListener('visibilitychange', updateAuto, { signal });
    const onMotionChange = () => { stopManual(); updateAuto(); };
    if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', onMotionChange);
    else reducedMotion.addListener?.(onMotionChange);

    function cleanup() {
      if (disposed) return;
      disposed = true;
      stopAuto();
      stopManual();
      cancelAnimationFrame(layoutFrame);
      clearTimeout(scrollTimer);
      visibilityObserver?.disconnect();
      resizeObserver?.disconnect();
      listeners.abort();
      if (reducedMotion.removeEventListener) reducedMotion.removeEventListener('change', onMotionChange);
      else reducedMotion.removeListener?.(onMotionChange);
    }
    document.addEventListener('shopify:section:unload', event => {
      if (event.target === root || event.target.contains(root)) cleanup();
    }, { signal });

    if (originals.length < 2) {
      ready = true;
      root.classList.add('is-ready');
    } else requestMetrics();
  }

  function init(context) {
    context.querySelectorAll('[data-kn-testimonial-carousel]').forEach(initCarousel);
    if (context.matches?.('[data-kn-testimonial-carousel]')) initCarousel(context);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();

(() => {
 const reduced = matchMedia('(prefers-reduced-motion: reduce)');
 let observer;
 function mount() {
  observer?.disconnect();
  const photos=[...document.querySelectorAll('[data-kinora-float]')];
  const update=()=>photos.forEach(photo=>photo.classList.toggle('is-visible',photo.dataset.inView==='true'&&!document.hidden&&!reduced.matches));
  observer=new IntersectionObserver(entries=>{entries.forEach(entry=>entry.target.dataset.inView=String(entry.isIntersecting));update();});
  photos.forEach(photo=>observer.observe(photo));
  document.addEventListener('visibilitychange',update,{signal:controller.signal});
  reduced.addEventListener('change',update,{signal:controller.signal});
 }
 let controller=new AbortController();
 const remount=()=>{controller.abort();controller=new AbortController();mount();};
 if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',mount,{once:true});else mount();
 document.addEventListener('shopify:section:load',remount);
 document.addEventListener('shopify:section:unload',()=>{observer?.disconnect();controller.abort();});
})();
